import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ANALYTICS_EVENTS,
  QUESTIONS,
  TOTAL_QUESTIONS,
  getContent,
  type Answer,
  type Answers,
  type Locale,
  type Question,
} from '@neke/core';
import type { ApiError } from '../api/client';
import { api, isConsentRequired } from '../api/client';
import { markConsentMissing, useSession } from '../store/session';
import { routeOf, track } from '../analytics';

/**
 * Отмечаем не каждый ответ, а четверти пути: вопросов ~сотня, и событие на
 * каждый съело бы месячную квоту проекта за считанные дни.
 */
const MILESTONES = [25, 50, 75] as const;

/** Тексты вариантов для конкретного вопроса — из словаря ядра, не из локали фронта. */
function optionsFor(question: Question, locale: Locale): Array<{ label: string; hint?: string }> {
  const c = getContent(locale);

  if (question.kind === 'likert') {
    return c.likertScale.map((label, i) => ({ label, hint: c.likertHints[i] || undefined }));
  }
  if (question.kind === 'cond') {
    return c.levels.map((label, i) => ({ label, hint: c.levelHints[i] }));
  }
  if (question.kind === 'fork') {
    return c.forks[question.fork].options.map((label) => ({ label }));
  }
  if (question.kind === 'pick') {
    if (question.id === 'self.faith') return c.scales.faith.map((label) => ({ label }));
    if (question.id === 'self.lang') return c.scales.lang.map((label) => ({ label }));
    if (question.id === 'self.edu') return c.scales.edu.map((label) => ({ label }));
    if (question.id === 'self.past') return c.scales.past.map((label) => ({ label }));
    if (question.id === 'self.field') return c.scales.field.map((label) => ({ label }));
    const want = c.want[question.id];
    return want ? want.options.map((label) => ({ label })) : [];
  }
  return [];
}

function titleFor(question: Question, locale: Locale): string {
  const c = getContent(locale);
  if (question.kind === 'likert') return c.likert[question.id] ?? question.id;
  if (question.kind === 'cond') return c.conditions[question.cond].title;
  if (question.kind === 'fork') return c.forks[question.fork].title;
  return c.self[question.id]?.title ?? c.want[question.id]?.title ?? question.id;
}

function hintFor(question: Question, locale: Locale, t: (k: string) => string): string {
  const c = getContent(locale);
  if (question.kind === 'likert') return t('test.hintLikert');
  if (question.kind === 'cond') return `${c.conditions[question.cond].hint} ${t('test.hintCond')}`;
  if (question.kind === 'fork') return t('test.hintFork');
  return c.self[question.id]?.hint ?? c.want[question.id]?.hint ?? '';
}

/** Куда уйти после теста. Принимаем только свои пути — не открытый редирект. */
function safeNext(value: string | null): string {
  if (!value || !value.startsWith('/') || value.startsWith('//')) return '/result';
  return value;
}

export function Test() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, locale, ready } = useSession();
  // Тест могли открыть по ссылке пары: тогда в конце возвращаемся к отчёту.
  const next = safeNext(params.get('next'));
  const selfPath = `/test?next=${encodeURIComponent(next)}`;

  const [assessmentId, setAssessmentId] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Answers>(() => new Array(TOTAL_QUESTIONS).fill(null));
  const [index, setIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const pending = useRef<Array<{ index: number; value: Answer }>>([]);
  const flushTimer = useRef<number | null>(null);
  const startedAt = useRef<number>(Date.now());
  const lastMilestone = useRef(0);

  /**
   * Сервер закрыл анкету до согласия. Человек попал сюда, потому что в сторе
   * `hasConsent` был true, — чиним представление и уводим подписывать, чтобы
   * вернуться потом ровно на это же место.
   */
  const toConsent = () => {
    markConsentMissing();
    navigate(`/onboarding?next=${encodeURIComponent(selfPath)}`);
  };

  useEffect(() => {
    if (!ready) return;
    if (!user) {
      navigate(`/login?next=${encodeURIComponent(selfPath)}`);
      return;
    }
    // Без пола и согласия анкету всё равно не завершить — спрашиваем сразу.
    if (!user.sex || !user.hasConsent) {
      navigate(`/onboarding?next=${encodeURIComponent(selfPath)}`);
      return;
    }
    void api
      .startAssessment()
      .then((draft) => {
        setAssessmentId(draft.id);
        setAnswers(draft.answers);
        // Возвращаем на первый неотвеченный пункт. Необязательные тут не в
        // счёт: сервер их всё равно не отличает, а перепрыгнуть пункт значило
        // бы оставить человека с анкетой, которую не завершить (см. FreeInput).
        const firstUnanswered = draft.answers.findIndex((a) => a === null);
        const from = firstUnanswered < 0 ? 0 : firstUnanswered;
        setIndex(from);
        startedAt.current = Date.now();
        // Черновик один на пользователя: продолжение — не новый старт.
        lastMilestone.current = Math.floor(((draft.answered / TOTAL_QUESTIONS) * 100) / 25) * 25;
        void track(ANALYTICS_EVENTS.testStarted, {
          questions_total: TOTAL_QUESTIONS,
          resumed: draft.answered > 0,
          answered_before: draft.answered,
          return_route: routeOf(next),
        });
      })
      .catch((e: ApiError) => {
        void track(ANALYTICS_EVENTS.testFailed, { stage: 'start', code: e.code });
        if (isConsentRequired(e)) {
          toConsent();
          return;
        }
        setError(e.message);
      });
    // toConsent держится на тех же navigate и selfPath, что уже перечислены.
  }, [ready, user, navigate, selfPath]);

  /** Ответы копим и отправляем пачкой: на мобильной сети это заметно экономнее. */
  const flush = () => {
    if (!assessmentId || pending.current.length === 0) return;
    const batch = pending.current;
    pending.current = [];
    setSaving(true);
    void api
      .saveAnswers(assessmentId, batch)
      .catch((e: unknown) => {
        // Отказ по согласию не лечится повтором: очередь копила бы ответы,
        // которые сервер не примет, — уводим подписывать.
        if (isConsentRequired(e)) {
          toConsent();
          return;
        }
        // Остальное не потеряем: вернём в очередь и попробуем со следующим ответом.
        pending.current.unshift(...batch);
      })
      .finally(() => setSaving(false));
  };

  const record = (value: Answer) => {
    const next = [...answers];
    next[index] = value;
    setAnswers(next);
    pending.current.push({ index, value });

    if (flushTimer.current) window.clearTimeout(flushTimer.current);
    flushTimer.current = window.setTimeout(flush, 600);

    const percent = ((index + 1) / TOTAL_QUESTIONS) * 100;
    for (const m of MILESTONES) {
      if (percent >= m && lastMilestone.current < m) {
        lastMilestone.current = m;
        void track(ANALYTICS_EVENTS.testProgress, {
          percent: m,
          question_index: index + 1,
          questions_total: TOTAL_QUESTIONS,
          elapsed_sec: Math.round((Date.now() - startedAt.current) / 1000),
        });
      }
    }

    if (index < TOTAL_QUESTIONS - 1) {
      setIndex(index + 1);
      window.scrollTo({ top: 0 });
    } else {
      finish(next);
    }
  };

  const finish = (finalAnswers: Answers) => {
    if (!assessmentId) return;
    flush();
    setSaving(true);
    void api
      .saveAnswers(
        assessmentId,
        finalAnswers.map((value, i) => ({ index: i, value })),
      )
      .then(() => api.completeAssessment(assessmentId))
      .then(() => {
        void track(ANALYTICS_EVENTS.testCompleted, {
          questions_total: TOTAL_QUESTIONS,
          answered: finalAnswers.filter((a) => a !== null).length,
          elapsed_sec: Math.round((Date.now() - startedAt.current) / 1000),
          next_route: routeOf(next),
        });
        navigate(next, { replace: true });
      })
      .catch((e: ApiError) => {
        void track(ANALYTICS_EVENTS.testFailed, { stage: 'complete', code: e.code });
        if (isConsentRequired(e)) {
          toConsent();
          return;
        }
        setError(e.message);
      })
      .finally(() => setSaving(false));
  };

  const question = QUESTIONS[index];
  const options = useMemo(
    () => (question ? optionsFor(question, locale) : []),
    [question, locale],
  );

  if (error) {
    return (
      <div className="page">
        <div className="card">
          <p>{error}</p>
          <button className="btn btn-ghost" onClick={() => window.location.reload()}>
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  if (!question || !assessmentId) {
    return (
      <div className="page">
        <p className="muted">{t('common.loading')}</p>
      </div>
    );
  }

  const partLabel = getContent(locale).parts[question.part];
  const current = answers[index];

  return (
    <div className="page">
      <div className="progress-bar">
        <div className="progress-meta">
          <span>{partLabel}</span>
          <b>
            {index + 1} {t('test.of')} {TOTAL_QUESTIONS}
          </b>
        </div>
        <div className="rail">
          <i style={{ width: `${(index / TOTAL_QUESTIONS) * 100}%` }} />
        </div>
      </div>

      <div className="stack">
        <p className="question">{titleFor(question, locale)}</p>
        <p className="fine">{hintFor(question, locale, t)}</p>
      </div>

      {question.kind === 'number' || question.kind === 'text' ? (
        <FreeInput
          key={question.id}
          question={question}
          value={typeof current === 'string' || typeof current === 'number' ? String(current) : ''}
          onSubmit={record}
          labels={{ next: t('test.next') }}
          placeholder={getContent(locale).self[question.id]?.placeholder ?? ''}
        />
      ) : (
        <div className="options">
          {options.map((option, i) => (
            <button
              key={i}
              className="option"
              aria-pressed={current === i + 1}
              onClick={() => record(i + 1)}
            >
              <span className="k">{i + 1}</span>
              <span>{option.label}</span>
              {option.hint && <span className="hint">{option.hint}</span>}
            </button>
          ))}
        </div>
      )}

      <div className="btn-row">
        <button
          className="btn btn-ghost"
          disabled={index === 0}
          onClick={() => {
            setIndex(index - 1);
            window.scrollTo({ top: 0 });
          }}
        >
          ← {t('test.back')}
        </button>
        <span className="fine hint-right">
          {saving ? t('test.saving') : t('test.saved')}
        </span>
      </div>

    </div>
  );
}

/**
 * Свободный ввод: возраст (`number`) и город (`text`).
 *
 * Кнопки «Пропустить» здесь нет, хотя `TextQuestion.optional` в ядре есть.
 * Причина в том, что стороны не договорились о значении пустой строки:
 * `isComplete()` и `answeredCount()` в ядре считают пропущенный необязательный
 * пункт отвеченным, а `completeAssessment` на сервере отвергает `''` наравне с
 * `null` — и анкету с пропуском не завершить вовсе. Пока все вопросы идут с
 * `optional: false`, спор спит; кнопка, которая заряжает его снова, не нужна.
 * Появится по-настоящему необязательный вопрос — договариваться начинают с
 * сервера, а здесь это одна строка:
 *
 *   {optional && <button className="btn btn-ghost" onClick={() => onSubmit('')}>…</button>}
 *
 * Подпись к ней (`test.skip`) в обоих словарях сохранена. То же решение принято
 * в мобильном приложении — `features/assessment/components/free-input.tsx`.
 */

interface FreeInputProps {
  question: Question;
  value: string;
  placeholder: string;
  labels: { next: string };
  onSubmit: (value: Answer) => void;
}

function FreeInput({ question, value, placeholder, labels, onSubmit }: FreeInputProps) {
  const [draft, setDraft] = useState(value);
  const isNumber = question.kind === 'number';

  const submit = () => {
    const trimmed = draft.trim();
    if (isNumber) {
      const n = Number.parseInt(trimmed, 10);
      const min = question.kind === 'number' ? question.min : 0;
      const max = question.kind === 'number' ? question.max : 200;
      if (Number.isNaN(n) || n < min || n > max) return;
      onSubmit(n);
      return;
    }
    // Пустая строка серверу не годится ни для какого пункта, даже помеченного
    // необязательным: см. комментарий выше.
    if (!trimmed) return;
    onSubmit(trimmed);
  };

  return (
    <div className="stack">
      <input
        type={isNumber ? 'number' : 'text'}
        inputMode={isNumber ? 'numeric' : 'text'}
        value={draft}
        placeholder={placeholder}
        autoFocus
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') submit();
        }}
      />
      <div className="btn-row">
        <button className="btn btn-primary" onClick={submit}>
          {labels.next}
        </button>
      </div>
    </div>
  );
}
